import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedRichText, LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import {
  ArrowRight,
  BadgeCheck,
  ChartNoAxesColumnIncreasing,
  CheckCheck,
  Compass,
  Download,
  Info,
  Repeat,
  ScanEye,
  TriangleAlert,
  Zap,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  billingAPI,
  onboardingGuideAPI,
  promptsAPI,
  visabilityAPI,
  type PromptCluster,
} from "../services/api";
import type { Brand } from "@/components/AppSidebar";
import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import { useMemo, useRef, useState } from "react";
import { useAuth } from "@/components/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { capturePosthogEvent } from "@/lib/posthog";
import {
  getCooldownBlockedMessage,
  getPromptRunsBlockedMessage,
  getTrialBoostBlockedInfo,
} from "@/lib/clusterBoost";
import { toast } from "sonner";
import "./style.css";
import { Separator } from "@/components/ui/separator";
import strategyIcon from "@/assets/strategy.svg";
import blurIcon from "@/assets/blur.png";
import { StrategyContent } from "@/components/StrategyContent";
import { BorderLoader } from "@/components/Animation";
import { canonicalizeDomain } from "@/utils/domain";
import RotatingText from "@/components/RotatingText";
import animatedLogo from "@/assets/animatedLogo.svg";
import { BorderLoaderLogo } from "@/components/AnimationLogo";
import { resolvePlanType } from "@/lib/planTier";
import { isQueuedOrRunningPrompt } from "./promptsMetricLoading";
import {
  resolveStrategyDisplayState,
  type RegenerateStatus,
} from "./strategy/strategy-flow";
import { usePromptsRealtime } from "@/hooks/usePromptsRealtime";
import { useVisibilityRealtimeInvalidation } from "@/hooks/useVisibilityRealtimeInvalidation";

const DEFAULT_STRATEGY_DAYS = 30;
const STRATEGY_EVIDENCE_WAIT_TIMEOUT_MS = 120000;
const STRATEGY_RUNNING_STALE_TIMEOUT_MS = 12 * 60 * 1000;
const BOOST_RUNNING_STATUSES = new Set(["generating", "queued"]);
const STRATEGY_LOADING_CLUSTER_STATUSES = new Set([
  "loading",
  "queued",
  "generating",
]);
const CLUSTER_ORDER_STORAGE_PREFIX = "cluster-actions:order";

const formatPlanUpdatedAt = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

const resolveClusterVisibilityScore = (
  cluster: PromptCluster,
): number | null => {
  const activePrompts = Array.isArray(cluster?.prompts)
    ? cluster.prompts.filter((prompt: any) => prompt?.is_active !== false)
    : [];
  const promptVisibilities = activePrompts
    .map((prompt) => Number(prompt?.visibility))
    .filter((value) => Number.isFinite(value)) as number[];
  if (!promptVisibilities.length) return null;
  const total = promptVisibilities.reduce((sum, value) => sum + value, 0);
  return total / promptVisibilities.length;
};

const isClusterFullyVisible = (cluster: PromptCluster): boolean => {
  const visibility = resolveClusterVisibilityScore(cluster);
  return visibility !== null && visibility >= 100;
};

const getClusterBoostStartedAtMs = (cluster: PromptCluster): number | null => {
  const rawValue = cluster?.boostLastGeneratedAt;
  if (!rawValue) return null;
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
};

const isClusterActivelyRunningBoost = (cluster: PromptCluster): boolean => {
  const status = String(cluster?.boostStatus || "")
    .trim()
    .toLowerCase();
  if (!BOOST_RUNNING_STATUSES.has(status)) return false;
  const startedAtMs = getClusterBoostStartedAtMs(cluster);
  if (startedAtMs === null) return true;
  return Date.now() - startedAtMs <= STRATEGY_RUNNING_STALE_TIMEOUT_MS;
};

const isClusterMarkedAsRunning = (cluster: PromptCluster): boolean =>
  BOOST_RUNNING_STATUSES.has(
    String(cluster?.boostStatus || "")
      .trim()
      .toLowerCase(),
  );

const getRunningStrategyClusterId = (
  clusters: PromptCluster[],
): string | null => {
  const runningCluster = clusters.find(isClusterActivelyRunningBoost);
  return runningCluster?.id || null;
};

const isClusterStrategyLoading = (cluster: PromptCluster): boolean =>
  STRATEGY_LOADING_CLUSTER_STATUSES.has(
    String(cluster?.strategyStatus || "")
      .trim()
      .toLowerCase(),
  );

const normalizeClusterStatus = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase();

const hasGeneratedTasks = (cluster: PromptCluster): boolean => {
  const taskCount = Number(cluster?.boostTaskCount);
  return Number.isFinite(taskCount) && taskCount > 0;
};

const isClusterStrategyReady = (cluster: PromptCluster): boolean => {
  const strategyStatus = normalizeClusterStatus(cluster?.strategyStatus);
  if (strategyStatus === "ready") return true;
  const boostStatus = normalizeClusterStatus(cluster?.boostStatus);
  return boostStatus === "ready" && hasGeneratedTasks(cluster);
};

const isClusterStrategyGenerateCandidate = (
  cluster: PromptCluster,
): boolean => {
  if (isClusterFullyVisible(cluster)) return false;
  if (isClusterStrategyReady(cluster)) return false;
  if (isClusterActivelyRunningBoost(cluster)) return false;
  if (isClusterStrategyLoading(cluster)) return false;
  return true;
};

const hasClusterStrategyStatus = (cluster: PromptCluster): boolean =>
  Boolean(
    String(cluster?.strategyStatus || "")
      .trim()
      .toLowerCase(),
  );

const isOwnBrandCompetitorRow = (
  row: Record<string, unknown>,
  currentBrandDomain: string | null,
): boolean => {
  if (Boolean(row?.is_main)) return true;
  const rowDomain = canonicalizeDomain(
    String(row?.brand_domain || row?.domain || ""),
  );
  return Boolean(
    currentBrandDomain && rowDomain && rowDomain === currentBrandDomain,
  );
};

const isObjectRecord = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null;

const getNestedPayload = (payload: unknown): Record<string, any> => {
  if (!isObjectRecord(payload)) return {};
  if (isObjectRecord(payload.message)) {
    return {
      ...payload,
      ...payload.message,
    };
  }
  return payload;
};

const isReadyBoostStatus = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase() === "ready";

const sortByStoredClusterOrder = <T extends { id?: string | null }>(
  clusters: T[],
  storageKey: string | null,
): T[] => {
  if (!clusters.length || !storageKey) return clusters;

  try {
    const storedOrderRaw = sessionStorage.getItem(storageKey);
    const parsedOrder = JSON.parse(storedOrderRaw || "[]");
    const storedOrder = Array.isArray(parsedOrder)
      ? (parsedOrder as unknown[])
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      : [];

    const orderMap = new Map<string, number>();
    storedOrder.forEach((id, index) => {
      if (!orderMap.has(id)) orderMap.set(id, index);
    });

    let nextIndex = orderMap.size;
    const nextStoredOrder = [...storedOrder];
    clusters.forEach((cluster) => {
      const id = String(cluster?.id || "").trim();
      if (!id || orderMap.has(id)) return;
      orderMap.set(id, nextIndex++);
      nextStoredOrder.push(id);
    });

    if (nextStoredOrder.length !== storedOrder.length) {
      sessionStorage.setItem(storageKey, JSON.stringify(nextStoredOrder));
    }

    const inputIndexById = new Map<string, number>();
    clusters.forEach((cluster, index) => {
      const id = String(cluster?.id || "").trim();
      if (!id) return;
      if (!inputIndexById.has(id)) inputIndexById.set(id, index);
    });

    return clusters.slice().sort((left, right) => {
      const leftId = String(left?.id || "").trim();
      const rightId = String(right?.id || "").trim();
      const leftOrder = orderMap.get(leftId) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = orderMap.get(rightId) ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      const leftInputIndex = inputIndexById.get(leftId) ?? 0;
      const rightInputIndex = inputIndexById.get(rightId) ?? 0;
      return leftInputIndex - rightInputIndex;
    });
  } catch {
    return clusters;
  }
};

export default function StrategyPage() {
  const { locale, t } = useLocale();
  const { currentBrand, selectedProviders, timeRange } = useOutletContext<{
    currentBrand: Brand;
    isTrialEnded: boolean;
    selectedProviders?: string[];
    timeRange?: string;
  }>();
  const strategyDays = React.useMemo(() => {
    const parsed = Number.parseInt(
      String(timeRange || DEFAULT_STRATEGY_DAYS),
      10,
    );
    return [1, 7, 14, 30, 90].includes(parsed) ? parsed : DEFAULT_STRATEGY_DAYS;
  }, [timeRange]);
  const resolvedBrandId =
    (currentBrand as any)?.brandId || (currentBrand as any)?.id;
  const currentBrandDomain = React.useMemo(
    () => canonicalizeDomain(currentBrand?.url || ""),
    [currentBrand?.url],
  );
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(
    null,
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [isBoostLimitModalOpen, setIsBoostLimitModalOpen] = useState(false);
  const [hasManualClusterInteraction, setHasManualClusterInteraction] =
    useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [evidenceWaitExpired, setEvidenceWaitExpired] = useState(false);
  const [optimisticTrialAllowedClusterId, setOptimisticTrialAllowedClusterId] =
    useState<string | null>(null);
  const [hasSeenTasksSection, setHasSeenTasksSection] = useState(false);
  const [searchParams] = useSearchParams();
  const isGuideTasksEntry = false;
  const previousSelectedClusterStatusRef = useRef<string | null>(null);
  const previousSnapshotReadyRef = useRef(false);
  const readyRefetchTimeoutRef = useRef<number | null>(null);
  const trackedStrategyViewedRef = useRef<string>("");
  const trackedStrategyTasksReadyRef = useRef<string>("");
  const trackedStrategyTasksSectionSeenRef = useRef<string>("");
  const completingBoostStepRef = useRef(false);
  const completedBoostStepRef = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const resolvedOrgId =
    ((user as any)?.company?.id as string | undefined) ||
    ((user as any)?.companyId as string | undefined) ||
    null;
  const billingSubscriptionQueryKey = [
    "billing-subscription",
    (user as any)?.id || "unknown",
  ] as const;
  const { data: billingSubscription, isFetched: billingSubscriptionFetched } =
    useQuery({
      queryKey: billingSubscriptionQueryKey,
      queryFn: () => billingAPI.getSubscription(),
      enabled: Boolean((user as any)?.id),
      staleTime: 60000,
    });
  const planType = resolvePlanType(
    (user as any)?.tier,
    billingSubscription?.subscription_status,
  );
  const normalizedUserTier = String((user as any)?.tier || "")
    .trim()
    .toLowerCase();
  const isStarterTierToken =
    normalizedUserTier === "1" || normalizedUserTier === "starter";
  const isWaitingForStarterBillingStatus = Boolean(
    (user as any)?.id && isStarterTierToken && !billingSubscriptionFetched,
  );
  const clusterOrderStorageKey = useMemo(
    () =>
      resolvedBrandId
        ? `${CLUSTER_ORDER_STORAGE_PREFIX}:${resolvedBrandId}`
        : null,
    [resolvedBrandId],
  );

  const clustersQueryKey = ["prompts", resolvedBrandId] as const;
  const { data: clustersResponse = [], isFetching: clustersFetching } =
    useQuery({
      queryKey: clustersQueryKey,
      queryFn: async () => {
        if (!resolvedBrandId || !currentBrand) return [];
        const response = await promptsAPI.getPrompts(
          { ...currentBrand, brandId: resolvedBrandId },
          [],
          [],
        );
        return Array.isArray(response?.clusters) ? response.clusters : [];
      },
      enabled: Boolean(resolvedBrandId && currentBrand),
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  usePromptsRealtime({
    brandId: resolvedBrandId,
    queryKey: clustersQueryKey,
    enabled: Boolean(resolvedBrandId && currentBrand),
  });

  const clustersData = useMemo<PromptCluster[]>(
    () =>
      sortByStoredClusterOrder(
        Array.isArray(clustersResponse) ? clustersResponse : [],
        clusterOrderStorageKey,
      ),
    [clusterOrderStorageKey, clustersResponse],
  );
  const selectedCluster = useMemo(
    () =>
      (selectedClusterId
        ? clustersData.find((cluster) => cluster.id === selectedClusterId)
        : null) || null,
    [clustersData, selectedClusterId],
  );
  const runningStrategyClusterId = useMemo(
    () => getRunningStrategyClusterId(clustersData),
    [clustersData],
  );
  const explicitTrialAllowedClusterIdFromClusters = useMemo(
    () =>
      clustersData.find((cluster) => cluster?.trial_allowed_cluster_id)
        ?.trial_allowed_cluster_id || null,
    [clustersData],
  );
  const trialAllowedClusterIdFromClustersHint = useMemo(
    () =>
      explicitTrialAllowedClusterIdFromClusters ||
      runningStrategyClusterId ||
      clustersData.find((cluster) => isReadyBoostStatus(cluster?.boostStatus))
        ?.id ||
      null,
    [
      clustersData,
      explicitTrialAllowedClusterIdFromClusters,
      runningStrategyClusterId,
    ],
  );
  const trialLockedClusterFromHint = useMemo(
    () =>
      trialAllowedClusterIdFromClustersHint
        ? clustersData.find(
            (cluster) => cluster.id === trialAllowedClusterIdFromClustersHint,
          ) || null
        : null,
    [clustersData, trialAllowedClusterIdFromClustersHint],
  );
  const canAutoAdvanceTrialCluster = Boolean(
    planType === "trial" &&
      trialAllowedClusterIdFromClustersHint &&
      trialLockedClusterFromHint &&
      isClusterFullyVisible(trialLockedClusterFromHint),
  );
  const requestedClusterId =
    String(searchParams.get("cluster") || "").trim() || null;
  const guidePreferredClusterId = useMemo(() => {
    if (!isGuideTasksEntry || !clustersData.length) return null;
    const loadingCluster =
      clustersData.find(
        (cluster) =>
          isClusterMarkedAsRunning(cluster) ||
          isClusterActivelyRunningBoost(cluster) ||
          isClusterStrategyLoading(cluster),
      ) || null;
    if (loadingCluster) return loadingCluster.id;
    const readyCluster = clustersData.find(isClusterStrategyReady) || null;
    if (readyCluster) return readyCluster.id;
    const generateCluster =
      clustersData.find(isClusterStrategyGenerateCandidate) || null;
    if (generateCluster) return generateCluster.id;
    return null;
  }, [clustersData, isGuideTasksEntry]);
  const defaultAutoBoostClusterId = useMemo(() => {
    if (!clustersData.length) return null;
    const readyCluster = clustersData.find(isClusterStrategyReady) || null;
    const firstClusterWithStrategyStatus =
      clustersData.find((cluster) => hasClusterStrategyStatus(cluster)) || null;
    if (!isGuideTasksEntry && requestedClusterId) {
      const requestedCluster = clustersData.find(
        (cluster) => cluster.id === requestedClusterId,
      );
      if (requestedCluster && !readyCluster) return requestedCluster.id;
    }
    return (
      readyCluster?.id ||
      firstClusterWithStrategyStatus?.id ||
      clustersData[0]?.id ||
      null
    );
  }, [clustersData, isGuideTasksEntry, requestedClusterId]);

  React.useEffect(() => {
    trackedStrategyViewedRef.current = "";
    trackedStrategyTasksReadyRef.current = "";
    trackedStrategyTasksSectionSeenRef.current = "";
    completingBoostStepRef.current = false;
    completedBoostStepRef.current = false;
    previousSelectedClusterStatusRef.current = null;
    previousSnapshotReadyRef.current = false;
    if (readyRefetchTimeoutRef.current !== null) {
      window.clearTimeout(readyRefetchTimeoutRef.current);
      readyRefetchTimeoutRef.current = null;
    }
    setOptimisticTrialAllowedClusterId(null);
    setHasManualClusterInteraction(false);
  }, [resolvedBrandId]);

  const allClustersAtFullVisibility = useMemo(
    () =>
      Boolean(clustersData.length) && clustersData.every(isClusterFullyVisible),
    [clustersData],
  );
  const autoBoostClusterId = allClustersAtFullVisibility
    ? null
    : defaultAutoBoostClusterId;
  const hasAnyGeneratedCluster = useMemo(
    () =>
      clustersData.some((cluster) => {
        const status = String(cluster?.boostStatus || "")
          .trim()
          .toLowerCase();
        const hasTasks =
          Number.isFinite(Number(cluster?.boostTaskCount)) &&
          Number(cluster?.boostTaskCount) > 0;
        return ["ready", "queued", "generating"].includes(status) || hasTasks;
      }),
    [clustersData],
  );
  React.useEffect(() => {
    if (!clustersData.length) {
      setSelectedClusterId(null);
      return;
    }
    const requestedCluster =
      !isGuideTasksEntry && requestedClusterId
        ? clustersData.find((cluster) => cluster.id === requestedClusterId) ||
          null
        : null;
    const guidePreferredCluster = guidePreferredClusterId
      ? clustersData.find(
          (cluster) => cluster.id === guidePreferredClusterId,
        ) || null
      : null;
    const selectedClusterStillExists = Boolean(
      selectedClusterId &&
        clustersData.some((cluster) => cluster.id === selectedClusterId),
    );

    if (!selectedClusterStillExists) {
      const requestedClusterIsLoadingOrRunning = Boolean(
        requestedCluster &&
          (isClusterMarkedAsRunning(requestedCluster) ||
            isClusterActivelyRunningBoost(requestedCluster) ||
            isClusterStrategyLoading(requestedCluster)),
      );
      const readyCluster =
        clustersData.find((cluster) => isClusterStrategyReady(cluster)) || null;
      const initialCluster =
        (requestedClusterIsLoadingOrRunning ? requestedCluster : null) ||
        readyCluster ||
        guidePreferredCluster ||
        requestedCluster ||
        clustersData.find((cluster) => hasClusterStrategyStatus(cluster)) ||
        clustersData[0] ||
        null;
      setSelectedClusterId(initialCluster?.id || null);
    } else if (
      isGuideTasksEntry &&
      !hasManualClusterInteraction &&
      guidePreferredCluster &&
      selectedClusterId !== guidePreferredCluster.id
    ) {
      setSelectedClusterId(guidePreferredCluster.id);
    } else if (
      !requestedClusterId &&
      !hasManualClusterInteraction &&
      selectedCluster &&
      isClusterFullyVisible(selectedCluster) &&
      defaultAutoBoostClusterId &&
      selectedCluster.id !== defaultAutoBoostClusterId &&
      (!hasAnyGeneratedCluster ||
        (canAutoAdvanceTrialCluster &&
          selectedCluster.id === trialAllowedClusterIdFromClustersHint))
    ) {
      const nextAutoCluster = clustersData.find(
        (cluster) => cluster.id === defaultAutoBoostClusterId,
      );
      if (nextAutoCluster) {
        setSelectedClusterId(nextAutoCluster.id);
      }
    }
  }, [
    clustersData,
    canAutoAdvanceTrialCluster,
    defaultAutoBoostClusterId,
    guidePreferredClusterId,
    hasAnyGeneratedCluster,
    hasManualClusterInteraction,
    isGuideTasksEntry,
    requestedClusterId,
    selectedCluster,
    selectedClusterId,
    trialAllowedClusterIdFromClustersHint,
  ]);
  React.useEffect(() => {
    setHasSeenTasksSection(false);
  }, [selectedClusterId]);
  const providersKey = (selectedProviders || []).join(",");
  const selectedClusterHasGeneratedBoost = Boolean(
    selectedCluster &&
      (["ready", "queued", "generating"].includes(
        String(selectedCluster.boostStatus || "")
          .trim()
          .toLowerCase(),
      ) ||
        Number(selectedCluster.boostTaskCount || 0) > 0),
  );

  const strategySnapshotQueryKey = [
    "strategySnapshot",
    resolvedBrandId,
    selectedClusterId,
    strategyDays,
    providersKey,
  ] as const;

  const strategyPlanQueryKey = [
    "strategyPlan",
    resolvedBrandId,
    selectedClusterId,
    strategyDays,
    providersKey,
    locale,
  ] as const;

  const { data: snapshotResponse, isLoading: snapshotInitialLoading } =
    useQuery({
      queryKey: strategySnapshotQueryKey,
      queryFn: async () => {
        if (!resolvedBrandId || !selectedClusterId) {
          throw new Error("Missing cluster context");
        }
        return visabilityAPI.getStrategySnapshot({
          brandId: resolvedBrandId,
          clusterId: selectedClusterId,
          providers: selectedProviders,
          days: strategyDays,
          autoQueue: false,
        });
      },
      enabled: Boolean(resolvedBrandId && selectedClusterId),
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const { data: planResponse, isLoading: planInitialLoading } = useQuery({
    queryKey: strategyPlanQueryKey,
    queryFn: async () => {
      if (!resolvedBrandId || !selectedClusterId) {
        throw new Error("Missing cluster context");
      }
      return visabilityAPI.getStrategyPlan({
        brandId: resolvedBrandId,
        clusterId: selectedClusterId,
        providers: selectedProviders,
        days: strategyDays,
        autoQueue: false,
      });
    },
    enabled: Boolean(resolvedBrandId && selectedClusterId),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const strategyRealtimeQueryKeys = useMemo(
    () => [strategySnapshotQueryKey, strategyPlanQueryKey, clustersQueryKey],
    [locale, providersKey, resolvedBrandId, selectedClusterId, strategyDays],
  );

  useVisibilityRealtimeInvalidation({
    brandId: resolvedBrandId,
    days: strategyDays,
    providers: selectedProviders,
    queryKeys: strategyRealtimeQueryKeys,
    enabled: Boolean(resolvedBrandId && selectedClusterId),
  });

  const strategySnapshot = snapshotResponse?.data || null;
  const strategyPlan = planResponse?.data || null;
  const isInitialStrategyDataFetchPending = Boolean(
    selectedClusterId &&
      (snapshotInitialLoading || planInitialLoading) &&
      !strategySnapshot &&
      !strategyPlan,
  );

  const planTasks = strategyPlan?.tasks || [];
  const persistedStrategyState =
    strategyPlan?.strategy_state || strategySnapshot?.strategy_state || null;
  const persistedStrategyStateStatus = String(
    persistedStrategyState?.status || "",
  )
    .trim()
    .toLowerCase();
  const persistedStrategyStateSource = String(
    persistedStrategyState?.source || "",
  )
    .trim()
    .toLowerCase();
  const regenerateStatus = (strategyPlan?.regenerate_status ||
    strategySnapshot?.regenerate_status ||
    "allowed") as RegenerateStatus;
  const nextRegenerateAt =
    strategyPlan?.next_regenerate_at ||
    strategySnapshot?.next_regenerate_at ||
    null;
  const cooldownDays =
    strategyPlan?.cooldown_days ?? strategySnapshot?.cooldown_days ?? null;
  const trialAllowedClusterIdFromClusters =
    planType === "trial" ? trialAllowedClusterIdFromClustersHint : null;

  const trialAllowedClusterIdFromApi =
    planType === "trial"
      ? strategyPlan?.trial_allowed_cluster_id ||
        strategySnapshot?.trial_allowed_cluster_id ||
        null
      : null;

  const trialAllowedClusterId =
    optimisticTrialAllowedClusterId ||
    trialAllowedClusterIdFromApi ||
    trialAllowedClusterIdFromClusters ||
    null;
  const isTrialRestrictedFlow =
    planType === "trial" && !isWaitingForStarterBillingStatus;
  React.useEffect(() => {
    if (
      !isTrialRestrictedFlow ||
      !selectedClusterId ||
      !selectedCluster ||
      !isClusterStrategyLoading(selectedCluster) ||
      trialAllowedClusterIdFromApi ||
      trialAllowedClusterIdFromClusters ||
      optimisticTrialAllowedClusterId
    ) {
      return;
    }
    setOptimisticTrialAllowedClusterId(selectedClusterId);
  }, [
    isTrialRestrictedFlow,
    optimisticTrialAllowedClusterId,
    selectedCluster,
    selectedClusterId,
    trialAllowedClusterIdFromApi,
    trialAllowedClusterIdFromClusters,
  ]);
  const trialBoostUsed = Boolean(
    isTrialRestrictedFlow &&
      (strategyPlan?.trial_boost_used ||
        strategySnapshot?.trial_boost_used ||
        trialAllowedClusterId),
  );
  const isTrialLockedCluster = Boolean(
    isTrialRestrictedFlow &&
      trialBoostUsed &&
      trialAllowedClusterId &&
      selectedClusterId &&
      trialAllowedClusterId !== selectedClusterId,
  );
  const selectedClusterBoostStatus = String(selectedCluster?.boostStatus || "")
    .trim()
    .toLowerCase();
  const isPersistedOnboardingStrategyLoading = Boolean(
    persistedStrategyStateStatus === "loading" &&
      persistedStrategyStateSource === "onboarding_auto",
  );
  const isPersistedStrategyQueuedOrGenerating = Boolean(
    persistedStrategyStateStatus === "queued" ||
      persistedStrategyStateStatus === "generating",
  );
  const isSelectedClusterStrategyLoading = Boolean(
    isPersistedOnboardingStrategyLoading ||
      isPersistedStrategyQueuedOrGenerating ||
      (selectedCluster && isClusterStrategyLoading(selectedCluster)),
  );
  const selectedClusterPromptRunStats = useMemo(() => {
    if (!selectedClusterId || !selectedCluster) {
      return { total: 0, queuedOrRunning: 0, blocked: false };
    }
    const prompts = Array.isArray(selectedCluster?.prompts)
      ? selectedCluster.prompts
      : [];
    const activePrompts = prompts.filter(
      (prompt: any) => prompt?.is_active !== false,
    );
    const total = activePrompts.length;
    const queuedOrRunning = activePrompts.filter((prompt: any) =>
      isQueuedOrRunningPrompt(prompt),
    ).length;
    const blocked = total > 0 && queuedOrRunning / total > 0.5;
    return { total, queuedOrRunning, blocked };
  }, [selectedCluster, selectedClusterId]);
  const selectedClusterHasActivePrompts =
    selectedClusterPromptRunStats.total > 0;
  const generateBlockedByPromptRuns = selectedClusterPromptRunStats.blocked;
  const generateBlockedByPromptRunsReason = generateBlockedByPromptRuns
    ? getPromptRunsBlockedMessage(
        selectedClusterPromptRunStats.queuedOrRunning,
        selectedClusterPromptRunStats.total,
      )
    : "";
  const selectedClusterVisibilityScore = useMemo(() => {
    if (!selectedCluster) return null;
    return resolveClusterVisibilityScore(selectedCluster);
  }, [selectedCluster]);
  const hasCoverageReady = Boolean(strategySnapshot?.coverage?.ready);
  const hasCompleteCoverage = useMemo(() => {
    const totalPrompts = Number(strategySnapshot?.coverage?.total_prompts);
    const completedPrompts = Number(
      strategySnapshot?.coverage?.completed_prompts,
    );
    if (
      !Number.isFinite(totalPrompts) ||
      !Number.isFinite(completedPrompts) ||
      totalPrompts <= 0
    ) {
      return false;
    }
    return completedPrompts >= totalPrompts;
  }, [
    strategySnapshot?.coverage?.completed_prompts,
    strategySnapshot?.coverage?.total_prompts,
  ]);
  const hasPlanTasks = planTasks.length > 0;
  const strategyTasksReady = Boolean(
    strategyPlan?.tasks_ready ||
      strategySnapshot?.plan?.tasks_ready ||
      hasPlanTasks,
  );
  const hasExternalCompetitorData = React.useMemo(() => {
    const competitors = Array.isArray(strategySnapshot?.competitors)
      ? strategySnapshot.competitors
      : [];
    return competitors.some(
      (row: Record<string, unknown>) =>
        !isOwnBrandCompetitorRow(row, currentBrandDomain),
    );
  }, [currentBrandDomain, strategySnapshot?.competitors]);
  const hasEvidenceData = Boolean(
    hasExternalCompetitorData ||
      (strategySnapshot?.cited_pages?.length || 0) > 0 ||
      (strategySnapshot?.cited_domains?.length || 0) > 0,
  );
  const hasCoverageSnapshot = Boolean(strategySnapshot?.coverage);
  const isSelectedClusterGenerating = Boolean(
    selectedCluster && isClusterActivelyRunningBoost(selectedCluster),
  );
  const isRegenerateRunning = Boolean(
    regenerateStatus === "queued" || regenerateStatus === "generating",
  );
  const shouldApplyPaidGenerateGuards = !isTrialRestrictedFlow;
  const {
    isSelectedClusterFullyVisible,
    hasGeneratedStrategyForSelectedCluster,
    shouldShowGenerateOverlay,
    isPlanLoading,
    shouldShowInitialGenerationLoader,
    shouldShowRefreshLoader,
    shouldShowStrategyProcessingState,
  } = resolveStrategyDisplayState({
    selectedClusterId,
    selectedClusterVisibilityScore,
    hasCoverageReady,
    hasCoverageSnapshot,
    hasCompleteCoverage,
    hasPlanTasks,
    strategyTasksReady,
    selectedClusterHasGeneratedBoost,
    isSelectedClusterGenerating,
    isRegenerateRunning,
    isSelectedClusterStrategyLoading,
    isPersistedOnboardingStrategyLoading,
    isInitialStrategyDataFetchPending,
    hasEvidenceData,
    evidenceWaitExpired,
    planInitialLoading,
    regenerateStatus,
  });
  const shouldBlockFullVisibilityForTrialBoostedCluster = Boolean(
    isSelectedClusterFullyVisible &&
      isTrialRestrictedFlow &&
      selectedClusterHasGeneratedBoost &&
      !isTrialLockedCluster,
  );
  const isSelectedClusterFullVisibilityBlocked = Boolean(
    isSelectedClusterFullyVisible &&
      (shouldApplyPaidGenerateGuards ||
        shouldBlockFullVisibilityForTrialBoostedCluster),
  );
  const shouldForceGenerateOverlayForTrialLockedCluster = Boolean(
    isTrialLockedCluster &&
      !isInitialStrategyDataFetchPending &&
      !isSelectedClusterStrategyLoading &&
      !isSelectedClusterFullVisibilityBlocked &&
      !isSelectedClusterGenerating &&
      !isRegenerateRunning,
  );
  const effectiveShouldShowGenerateOverlay = Boolean(
    shouldShowGenerateOverlay ||
      shouldForceGenerateOverlayForTrialLockedCluster,
  );
  const fullVisibilityStatusLabel =
    "This cluster already has full visibility. No actions needed";
  const fullVisibilityBlockedMessage =
    "No boost needed. This cluster is already fully covered.";
  const shouldShowAll100Message = Boolean(
    allClustersAtFullVisibility &&
      clustersData.length &&
      shouldApplyPaidGenerateGuards,
  );
  const isAnotherClusterGenerating = Boolean(
    runningStrategyClusterId &&
      selectedClusterId &&
      runningStrategyClusterId !== selectedClusterId,
  );
  const isAnotherClusterGeneratingBlocked =
    isTrialRestrictedFlow && isAnotherClusterGenerating;

  React.useEffect(() => {
    previousSelectedClusterStatusRef.current =
      selectedClusterBoostStatus || null;
    previousSnapshotReadyRef.current = false;
    if (readyRefetchTimeoutRef.current !== null) {
      window.clearTimeout(readyRefetchTimeoutRef.current);
      readyRefetchTimeoutRef.current = null;
    }
  }, [selectedClusterId]);

  const refetchBrandScopedQueries = React.useCallback(async () => {
    if (!resolvedBrandId) return;
    const strategyKeys = [
      strategySnapshotQueryKey,
      strategyPlanQueryKey,
      clustersQueryKey,
    ] as const;
    await Promise.all(
      strategyKeys.map((queryKey) =>
        queryClient.invalidateQueries({
          queryKey,
          refetchType: "active",
        }),
      ),
    );
  }, [
    clustersQueryKey,
    queryClient,
    resolvedBrandId,
    strategyPlanQueryKey,
    strategySnapshotQueryKey,
  ]);

  React.useEffect(() => {
    if (!resolvedBrandId || !selectedClusterId) return;
    const previousStatus = String(
      previousSelectedClusterStatusRef.current || "",
    )
      .trim()
      .toLowerCase();
    const currentStatus = selectedClusterBoostStatus;
    const transitionedToReady =
      currentStatus === "ready" && previousStatus && previousStatus !== "ready";

    if (!transitionedToReady) {
      previousSelectedClusterStatusRef.current = currentStatus || null;
      return;
    }

    void refetchBrandScopedQueries();
    if (readyRefetchTimeoutRef.current !== null) {
      window.clearTimeout(readyRefetchTimeoutRef.current);
    }
    readyRefetchTimeoutRef.current = window.setTimeout(() => {
      void refetchBrandScopedQueries();
    }, 8000);

    previousSelectedClusterStatusRef.current = currentStatus || null;

    return () => {
      if (readyRefetchTimeoutRef.current !== null) {
        window.clearTimeout(readyRefetchTimeoutRef.current);
        readyRefetchTimeoutRef.current = null;
      }
    };
  }, [
    refetchBrandScopedQueries,
    resolvedBrandId,
    selectedClusterBoostStatus,
    selectedClusterId,
  ]);

  React.useEffect(() => {
    if (!resolvedBrandId || !selectedClusterId) return;
    const hasSnapshotVisibility = Number.isFinite(
      Number(strategySnapshot?.visibility_score),
    );
    const snapshotReadyForRefresh = Boolean(
      hasCoverageReady &&
        (hasEvidenceData || strategyTasksReady || hasSnapshotVisibility),
    );

    if (!snapshotReadyForRefresh) {
      previousSnapshotReadyRef.current = false;
      return;
    }

    if (previousSnapshotReadyRef.current) return;
    previousSnapshotReadyRef.current = true;
    void queryClient.invalidateQueries({
      queryKey: clustersQueryKey,
      refetchType: "active",
    });
  }, [
    clustersQueryKey,
    hasCoverageReady,
    hasEvidenceData,
    queryClient,
    resolvedBrandId,
    selectedClusterId,
    strategySnapshot?.visibility_score,
    strategyTasksReady,
  ]);

  React.useEffect(() => {
    if (!selectedClusterId) {
      setEvidenceWaitExpired(false);
      return;
    }
    setEvidenceWaitExpired(false);
    const timeoutId = window.setTimeout(() => {
      setEvidenceWaitExpired(true);
    }, STRATEGY_EVIDENCE_WAIT_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [
    providersKey,
    regenerateStatus,
    selectedClusterBoostStatus,
    selectedClusterId,
    isSelectedClusterStrategyLoading,
  ]);

  const handleTasksSectionSeen = React.useCallback(() => {
    if (!selectedClusterId || !resolvedBrandId) return;
    setHasSeenTasksSection(true);
    const key = `${resolvedBrandId}:${selectedClusterId}`;
    if (trackedStrategyTasksSectionSeenRef.current === key) return;
    trackedStrategyTasksSectionSeenRef.current = key;
    capturePosthogEvent("strategy_tasks_section_seen", {
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
      cluster_id: selectedClusterId,
    });
  }, [resolvedBrandId, resolvedOrgId, selectedClusterId]);

  React.useEffect(() => {
    if (!resolvedBrandId || !selectedClusterId) return;
    const key = `${resolvedBrandId}:${providersKey}:${selectedClusterId}`;
    if (trackedStrategyViewedRef.current === key) return;
    trackedStrategyViewedRef.current = key;
    capturePosthogEvent("strategy_viewed", {
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
      cluster_id: selectedClusterId,
      providers: providersKey || "all",
    });
  }, [providersKey, resolvedBrandId, resolvedOrgId, selectedClusterId]);

  React.useEffect(() => {
    if (!resolvedBrandId || !selectedClusterId || !strategyTasksReady) return;
    const key = `${resolvedBrandId}:${selectedClusterId}`;
    if (trackedStrategyTasksReadyRef.current === key) return;
    trackedStrategyTasksReadyRef.current = key;
    capturePosthogEvent("strategy_tasks_ready", {
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
      cluster_id: selectedClusterId,
      tasks_count: planTasks.length,
    });
  }, [
    planTasks.length,
    resolvedBrandId,
    resolvedOrgId,
    selectedClusterId,
    strategyTasksReady,
  ]);

  React.useEffect(() => {
    if (
      !resolvedBrandId ||
      !strategyTasksReady ||
      !hasPlanTasks ||
      !hasSeenTasksSection
    ) {
      return;
    }
    if (completedBoostStepRef.current || completingBoostStepRef.current) return;

    completingBoostStepRef.current = true;
    void onboardingGuideAPI
      .updateState({
        brandId: resolvedBrandId,
        stepName: "boost_cluster",
        completionSource: "user",
      })
      .then((guideRes) => {
        if (guideRes.completedStepName) {
          capturePosthogEvent("onboarding_guide_step_completed", {
            step_name: guideRes.completedStepName,
            completion_source: guideRes.completionSource || "user",
          });
        }
        completedBoostStepRef.current = true;
        queryClient.invalidateQueries({
          queryKey: ["onboardingGuide", resolvedBrandId],
        });
      })
      .catch(() => {
        completingBoostStepRef.current = false;
      })
      .finally(() => {
        completingBoostStepRef.current = false;
      });
  }, [
    hasPlanTasks,
    hasSeenTasksSection,
    queryClient,
    resolvedBrandId,
    strategyTasksReady,
  ]);

  const planUpdatedAt = useMemo(() => {
    const latest = planTasks
      .map((task) => task.updated_at || task.created_at)
      .filter((value): value is string => Boolean(value))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
    return formatPlanUpdatedAt(latest);
  }, [planTasks]);

  const handleTrialBlockedPayload = (payload: unknown) => {
    if (!isTrialRestrictedFlow) return false;

    const parsedPayload = getNestedPayload(payload);
    const blockedInfo = getTrialBoostBlockedInfo(parsedPayload);
    if (blockedInfo.blocked) {
      setIsBoostLimitModalOpen(true);
      capturePosthogEvent("boost_cluster_blocked_trial_limit", {
        requested_cluster_id: selectedClusterId,
        trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
        brand_id: resolvedBrandId,
        org_id: resolvedOrgId,
        source: "strategy_page",
      });
      return true;
    }

    const code = String(parsedPayload?.code || "")
      .trim()
      .toUpperCase();
    if (code === "TRIAL_STRATEGY_LIMIT_REACHED") {
      setIsBoostLimitModalOpen(true);
      capturePosthogEvent("boost_cluster_blocked_trial_limit", {
        requested_cluster_id: selectedClusterId,
        trial_boosted_cluster_id:
          parsedPayload?.trial_allowed_cluster_id || null,
        brand_id: resolvedBrandId,
        org_id: resolvedOrgId,
        source: "strategy_page",
      });
      return true;
    }

    return false;
  };

  const regenerateMutation = useMutation({
    onMutate: () => {
      if (
        isTrialRestrictedFlow &&
        selectedClusterId &&
        !trialAllowedClusterIdFromApi &&
        !trialAllowedClusterIdFromClusters &&
        !optimisticTrialAllowedClusterId &&
        (!autoBoostClusterId || selectedClusterId === autoBoostClusterId)
      ) {
        setOptimisticTrialAllowedClusterId(selectedClusterId);
      }
    },
    mutationFn: async () => {
      if (!resolvedBrandId || !selectedClusterId) {
        throw new Error("Select a cluster first");
      }
      if ((user as any)?.id) {
        await queryClient.ensureQueryData({
          queryKey: billingSubscriptionQueryKey,
          queryFn: () => billingAPI.getSubscription(),
          staleTime: 0,
        });
      }
      return visabilityAPI.regenerateStrategy({
        brandId: resolvedBrandId,
        clusterId: selectedClusterId,
        providers: selectedProviders,
        days: strategyDays,
      });
    },
    onSuccess: async (response) => {
      if (!response?.ok) {
        if (handleTrialBlockedPayload(response)) return;
        if (response?.status === "cooldown") {
          toast.error(
            getCooldownBlockedMessage(
              response?.next_regenerate_at || null,
              response?.cooldown_days ?? null,
            ),
            { position: "bottom-right" },
          );
          return;
        }
        toast.error(response?.message || "Failed to regenerate strategy.", {
          position: "bottom-right",
        });
        return;
      }
      if (isTrialRestrictedFlow) {
        setOptimisticTrialAllowedClusterId(
          response?.trial_allowed_cluster_id || selectedClusterId || null,
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: strategySnapshotQueryKey }),
        queryClient.invalidateQueries({ queryKey: strategyPlanQueryKey }),
        queryClient.invalidateQueries({ queryKey: clustersQueryKey }),
      ]);
    },
    onError: (error: any) => {
      const payload = getNestedPayload(error?.response?.data || {});
      if (handleTrialBlockedPayload(payload)) return;
      if (
        String(payload?.status || "")
          .trim()
          .toLowerCase() === "cooldown"
      ) {
        toast.error(
          getCooldownBlockedMessage(
            payload?.next_regenerate_at || null,
            payload?.cooldown_days ?? null,
          ),
          { position: "bottom-right" },
        );
        return;
      }
      toast.error(
        payload?.message || error?.message || "Failed to regenerate strategy.",
        {
          position: "bottom-right",
        },
      );
      setOptimisticTrialAllowedClusterId(
        trialAllowedClusterIdFromApi ||
          trialAllowedClusterIdFromClusters ||
          null,
      );
    },
  });

  const isRegenerating =
    regenerateMutation.isPending ||
    regenerateStatus === "queued" ||
    regenerateStatus === "generating";
  const hasHistoricalStrategy = hasGeneratedStrategyForSelectedCluster;
  const regenerateDisabledReason = !selectedClusterId
    ? "Select a cluster first."
    : isSelectedClusterFullVisibilityBlocked
      ? fullVisibilityBlockedMessage
      : isTrialLockedCluster
        ? "Trial boost limit reached for this cluster. Upgrade to unlock additional clusters."
        : shouldApplyPaidGenerateGuards && !selectedClusterHasActivePrompts
          ? "This cluster has no active prompts to boost."
          : shouldApplyPaidGenerateGuards && generateBlockedByPromptRuns
            ? generateBlockedByPromptRunsReason
            : isAnotherClusterGeneratingBlocked
              ? "Another cluster is currently generating. Please wait until it finishes."
              : shouldShowStrategyProcessingState
                ? "Actions are still being prepared for this cluster. Please wait."
                : regenerateStatus === "cooldown"
                  ? getCooldownBlockedMessage(nextRegenerateAt, cooldownDays)
                  : "";
  const canRunRegenerate = Boolean(
    selectedClusterId &&
      !shouldShowStrategyProcessingState &&
      !isAnotherClusterGeneratingBlocked &&
      !isSelectedClusterFullVisibilityBlocked &&
      !isTrialLockedCluster &&
      (!shouldApplyPaidGenerateGuards ||
        (selectedClusterHasActivePrompts && !generateBlockedByPromptRuns)) &&
      regenerateStatus !== "cooldown" &&
      !isRegenerating,
  );
  const canClickGenerate = canRunRegenerate || isTrialLockedCluster;

  const handleRegenerate = () => {
    if (!selectedClusterId || !resolvedBrandId) {
      toast.error(t("legacy.select.a.cluster.first.10ho7ma"), {
        position: "bottom-right",
      });
      return;
    }
    setHasManualClusterInteraction(true);
    if (isTrialLockedCluster) {
      setIsBoostLimitModalOpen(true);
      capturePosthogEvent("boost_cluster_blocked_trial_limit", {
        requested_cluster_id: selectedClusterId,
        trial_boosted_cluster_id: trialAllowedClusterId,
        brand_id: resolvedBrandId,
        org_id: resolvedOrgId,
        source: "strategy_page",
      });
      return;
    }
    if (isAnotherClusterGeneratingBlocked) {
      toast.error(
        t(
          "legacy.another.cluster.is.currently.generating.please.wait.until.1nhxhjv",
        ),
        {
          position: "bottom-right",
        },
      );
      return;
    }
    if (shouldApplyPaidGenerateGuards && !selectedClusterHasActivePrompts) {
      toast.error(
        t("legacy.this.cluster.has.no.active.prompts.to.boost.9tjl5l"),
        {
          position: "bottom-right",
        },
      );
      return;
    }
    if (shouldApplyPaidGenerateGuards && generateBlockedByPromptRuns) {
      toast.error(generateBlockedByPromptRunsReason, {
        position: "bottom-right",
      });
      return;
    }
    if (shouldShowStrategyProcessingState) return;
    if (isSelectedClusterFullVisibilityBlocked) {
      toast.error(fullVisibilityBlockedMessage, { position: "bottom-right" });
      return;
    }
    if (regenerateStatus === "cooldown") {
      toast.error(getCooldownBlockedMessage(nextRegenerateAt, cooldownDays), {
        position: "bottom-right",
      });
      return;
    }
    if (isRegenerating) return;

    capturePosthogEvent("strategy_regenerate_clicked", {
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
      cluster_id: selectedClusterId,
    });
    regenerateMutation.mutate();
  };
  const handleExportPdf = () => {
    capturePosthogEvent("strategy_export_pdf_clicked", {
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
      cluster_id: selectedClusterId,
    });

    setIsPdfExporting(true);
    document.body.classList.add("strategy-pdf-exporting");

    const cleanup = () => {
      document.body.classList.remove("strategy-pdf-exporting");
      setIsPdfExporting(false);
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        try {
          window.print();
          window.setTimeout(cleanup, 30000);
        } catch {
          cleanup();
          toast.error(
            t("legacy.export.pdf.is.unavailable.in.this.browser.y9krk8"),
            {
              position: "bottom-right",
            },
          );
        }
      });
    });
  };
  const statusLabel = isSelectedClusterFullVisibilityBlocked
    ? fullVisibilityStatusLabel
    : planUpdatedAt
      ? `Plan updated on ${planUpdatedAt}`
      : "Plan pending";
  const shouldShowProcessingButton = shouldShowStrategyProcessingState;
  const shouldShowStrategyViewportLoader = Boolean(
    clustersData.length &&
      (!selectedClusterId ||
        !selectedCluster ||
        isInitialStrategyDataFetchPending) &&
      !shouldShowInitialGenerationLoader &&
      !shouldShowRefreshLoader &&
      !effectiveShouldShowGenerateOverlay,
  );
  const shouldHideHeaderActions =
    effectiveShouldShowGenerateOverlay ||
    shouldShowInitialGenerationLoader ||
    shouldShowStrategyViewportLoader;
  const strategyBuildingLoader = (
    <div className="relative min-h-[360px] overflow-hidden sm:min-h-[520px]">
      <img src={blurIcon} className="w-full h-full object-cover" alt="" />
      <div className="absolute inset-0 flex items-center justify-center bg-white/55 backdrop-blur-[2px]">
        <div className="flex w-[calc(100%-24px)] max-w-[520px] flex-col items-center gap-3 rounded-md bg-white px-4 py-5 text-center sm:px-6 sm:py-7">
          <div className="relative">
            <BorderLoaderLogo />
            <img src={animatedLogo} alt="" />
          </div>
          <div className="text-[#0A0A0A] text-2xl leading-8 font-semibold sm:text-[36px] sm:leading-[42px]">
            <LocalizedText id="legacy.building.your.ai.visibility.strategy.11ic5ef" />
          </div>
          <div className="text-sm text-[#737373]">
            <LocalizedText id="legacy.a.step.by.step.plan.to.help.your.1k8hkhx" />
          </div>
          <RotatingText />
        </div>
      </div>
    </div>
  );
  const defaultPageLoader = (
    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-2">
      <Spinner className="text-[#FF5E00]" variant="circle" size={20} />
      <div className="text-sm text-[#737373]">
        <LocalizedText id="legacy.processing.3jbdvy" />
      </div>
    </div>
  );

  return (
    <div className="strategy-export-page h-full pb-[70px]">
      <div className="mb-4 flex flex-col gap-3 rounded-md bg-[#F5F5F5] p-3 text-sm text-[#0A0A0A] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2 items-start sm:items-center font-medium">
          <img src={strategyIcon} alt="" />
          <span className="font-bold">
            <LocalizedText id="legacy.manage.your.actions.1awrql4" />
          </span>
          <span className="hidden sm:inline">
            {" "}
            <LocalizedText id="legacy.switch.to.the.tasks.board.to.track.progress.z8wkop" />
          </span>
          <span className="sm:hidden">
            {" "}
            <LocalizedText id="legacy.track.progress.and.keep.your.plan.moving.17pr1l1" />
          </span>
        </div>
        <Button
          variant="outline"
          className="cursor-pointer"
          onClick={() => {
            const params = new URLSearchParams();
            if (resolvedBrandId) {
              params.set("brandId", resolvedBrandId);
            }
            if (selectedClusterId) {
              params.set("cluster", selectedClusterId);
            }
            const query = params.toString();
            navigate(
              query
                ? `/dashboard/get-cited/cluster-actions?${query}`
                : "/dashboard/get-cited/cluster-actions",
            );
          }}
        >
          <LocalizedText id="legacy.open.task.board.ncs7ty" /> <ArrowRight />
        </Button>
      </div>

      <div className="strategy-export-layout flex h-full min-h-[0px] flex-col lg:flex-row">
        <div className="hidden w-full flex-col overflow-y-auto rounded-md border p-3 lg:mb-0 lg:mr-3 lg:flex lg:min-w-[264px] lg:max-w-[264px]">
          {clustersData.length ? (
            <div className="relative flex flex-col justify-between lg:h-full">
              <div>
                {clustersData.map((item) => {
                  const isOpen = openId === item.id;
                  const isClusterRunning = isClusterActivelyRunningBoost(item);
                  const showClusterProcessing =
                    isClusterRunning || isClusterStrategyLoading(item);
                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        setHasManualClusterInteraction(true);
                        setSelectedClusterId(item.id);
                      }}
                      className={`flex rounded-md justify-between p-2 items-center hover:bg-[#F5F5F5] ${selectedClusterId === item.id ? "bg-[#F5F5F5]" : ""}`}
                    >
                      <div
                        onPointerEnter={(event) => {
                          const titleElement =
                            event.currentTarget.querySelector(
                              '[data-cluster-title="true"]',
                            ) as HTMLDivElement | null;
                          const isTruncated = Boolean(
                            titleElement &&
                              titleElement.scrollWidth >
                                titleElement.clientWidth,
                          );
                          if (!isTruncated) {
                            if (openTimeoutRef.current) {
                              clearTimeout(openTimeoutRef.current);
                            }
                            setOpenId(null);
                            return;
                          }
                          openTimeoutRef.current = setTimeout(() => {
                            setOpenId(item.id);
                          }, 800);
                        }}
                        onPointerLeave={() => {
                          if (openTimeoutRef.current) {
                            clearTimeout(openTimeoutRef.current);
                          }
                          setOpenId(null);
                        }}
                        className="w-full max-w-[240px] cursor-pointer text-sm font-medium sm:max-w-[420px] lg:max-w-[195px]"
                      >
                        <Popover open={isOpen}>
                          <PopoverTrigger asChild>
                            <div
                              data-cluster-title="true"
                              className="w-full truncate"
                            >
                              {item.title}
                            </div>
                          </PopoverTrigger>

                          <PopoverContent
                            onClick={(event) => event.stopPropagation()}
                            className="transition-all duration-200 ease-out w-auto p-3 text-sm max-w-xs break-words"
                          >
                            {item.title}
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="text-[#737373] text-sm">
                        {showClusterProcessing ? (
                          <div className="p-1 rounded-md">
                            <Spinner
                              className="text-[#FF5E00]"
                              variant="circle-filled"
                              size={16}
                            />
                          </div>
                        ) : (
                          <div className="group relative flex items-center justify-center rounded-md cursor-pointer" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col gap-3">
                <Separator />
                <Button
                  onClick={() => {
                    navigate("/dashboard/prompts");
                  }}
                  variant="outline"
                  className="flex items-center justify-center w-full cursor-pointer"
                >
                  <LocalizedText id="legacy.manage.clusters.9r9u1p" />{" "}
                  <ArrowRight />
                </Button>
              </div>
            </div>
          ) : clustersFetching ? (
            <div className="relative h-[150px] flex flex-col gap-2">
              <div className="z-10 gap-1 flex-col absolute flex justify-center items-center w-full h-full">
                <div className="flex gap-2 items-center flex-col p-2 bg-muted rounded-md">
                  <Spinner className="text-[#FF5E00]" variant="circle" />
                </div>
                <div>
                  <LocalizedText id="legacy.processing.3jbdvy" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex grow">
              <div className="flex flex-col gap-3 items-center my-auto">
                <div className="p-2 rounded-md bg-[#F5F5F5]">
                  <Info size={24} strokeWidth={1.5} />
                </div>
                <div className="font-medium text-lg text-[#0A0A0A]">
                  <LocalizedText id="legacy.no.clusters.yet.1nsyylx" />
                </div>
                <div className="text-sm text-center leading-5 text-[#737373]">
                  <LocalizedText id="legacy.create.clusters.to.group.related.prompts.and.keep.1ewepbw" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="strategy-export-root w-full min-w-0 overflow-hidden flex flex-col">
          {clustersData.length ? (
            <div className="mb-3 rounded-md border p-2 lg:hidden">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {clustersData.map((item) => {
                  const isSelected = selectedClusterId === item.id;
                  const isClusterRunning = isClusterActivelyRunningBoost(item);
                  const showClusterProcessing =
                    isClusterRunning || isClusterStrategyLoading(item);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setHasManualClusterInteraction(true);
                        setSelectedClusterId(item.id);
                      }}
                      className={`inline-flex max-w-[240px] shrink-0 items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                        isSelected
                          ? "border-[#D4D4D4] bg-[#F5F5F5] text-[#0A0A0A]"
                          : "border-[#E5E5E5] bg-white text-[#404040]"
                      }`}
                    >
                      <span className="truncate">{item.title}</span>
                      {showClusterProcessing ? (
                        <Spinner
                          className="text-[#FF5E00]"
                          variant="circle-filled"
                          size={14}
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-2 sm:items-center">
              <div className="h-fit rounded-lg bg-[#FFEEE5] p-2">
                <Compass
                  strokeWidth={1}
                  size={32}
                  className="text-[#FF5E00] sm:w-9 sm:h-9"
                />
              </div>
              <div>
                <div className="text-lg font-semibold text-[#0A0A0A] sm:text-2xl">
                  <LocalizedText id="legacy.your.ai.visibility.strategy.1hz77nl" />
                </div>
                <div className="text-xs text-[#737373] sm:text-sm">
                  <LocalizedRichText
                    id="strategyPage.header.description"
                    values={{
                      brand: (
                        <span className="inline-flex items-baseline mx-1">
                          <span className="relative top-[6px] hidden aspect-square w-8 h-5 items-center justify-center rounded-lg sm:flex">
                            <img
                              className="w-6 rounded-md"
                              src={`https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://${canonicalizeDomain(currentBrand?.url) || ""}&size=64`}
                              alt="logo"
                            />
                          </span>
                          <span className="font-semibold text-[#FF5E00] capitalize">
                            {currentBrand?.name}
                          </span>
                        </span>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>

            {!shouldHideHeaderActions ? (
              <div className="strategy-export-hidden mr-0 flex flex-wrap items-center gap-2 lg:mr-2 lg:gap-3">
                <div className="flex max-w-full items-center gap-2 rounded-md bg-[#E6F6EB] p-2 text-sm">
                  {isSelectedClusterFullVisibilityBlocked ? (
                    <CheckCheck size={16} className="text-[#2B9A66]" />
                  ) : planUpdatedAt ? (
                    <CheckCheck size={16} className="text-[#2B9A66]" />
                  ) : (
                    <Info size={16} className="text-[#2B9A66]" />
                  )}
                  {statusLabel}
                </div>
                <div className="flex shrink-0 items-center gap-2 lg:gap-3">
                  {!isSelectedClusterFullVisibilityBlocked ? (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="relative inline-block">
                            {canRunRegenerate ? <BorderLoader /> : null}
                            <Button
                              className="h-9 cursor-pointer sm:h-10"
                              onClick={handleRegenerate}
                              disabled={!canClickGenerate}
                            >
                              {shouldShowProcessingButton ? (
                                <Spinner
                                  className="text-current"
                                  variant="circle"
                                  size={14}
                                />
                              ) : (
                                <Repeat />
                              )}
                              {hasHistoricalStrategy
                                ? "Re-generate"
                                : "Generate"}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {!canClickGenerate && regenerateDisabledReason ? (
                          <TooltipContent
                            side="top"
                            className="max-w-[260px] text-center"
                          >
                            {regenerateDisabledReason}
                          </TooltipContent>
                        ) : null}
                      </Tooltip>
                      <div
                        aria-hidden="true"
                        className="hidden h-10 w-px shrink-0 self-center bg-[#D4D4D4] sm:block"
                      />
                    </>
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={t("legacy.export.pdf.l6ub1v")}
                        className="h-9 w-9 cursor-pointer rounded-md border-[#E5E5E5] bg-white text-[#0A0A0A] shadow-sm hover:bg-[#F5F5F5] sm:h-10 sm:w-10"
                        onClick={handleExportPdf}
                      >
                        <Download size={18} strokeWidth={1.75} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      sideOffset={8}
                      className="bg-[#0A0A0A] px-3 py-2 text-sm text-white"
                    >
                      <LocalizedText id="legacy.export.pdf.l6ub1v" />
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ) : null}
          </div>

          {clustersData.length ? (
            <>
              {shouldShowAll100Message ? (
                <div className="mt-3 rounded-md border border-[#E5E5E5] bg-[#F5F5F5] px-4 py-3 text-sm text-[#404040]">
                  <LocalizedText id="legacy.all.clusters.are.already.at.100.visibility.strategy.18fntkr" />
                </div>
              ) : null}
              <div
                className={`strategy-export-card relative rounded-md p-4 w-full h-full mt-3 overflow-auto ${
                  shouldShowInitialGenerationLoader ? "" : "border"
                }`}
              >
                {shouldShowInitialGenerationLoader ? (
                  strategyBuildingLoader
                ) : shouldShowStrategyViewportLoader ? (
                  <div className="relative min-h-[360px] rounded-md border bg-white p-4 sm:min-h-[520px]">
                    {defaultPageLoader}
                  </div>
                ) : shouldShowRefreshLoader ? (
                  <div className="relative min-h-[360px] rounded-md border bg-white p-4 sm:min-h-[520px]">
                    <div className="z-10 absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="flex items-center justify-center rounded-md bg-white/70 p-2">
                        <Spinner
                          className="text-[#FF5E00]"
                          variant="circle"
                          size={16}
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Skeleton className="h-8 w-[180px]" />
                      <Skeleton className="h-10 w-[260px]" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-[90%]" />
                      <Skeleton className="h-[120px] w-full" />
                      <Skeleton className="h-[120px] w-full" />
                      <Skeleton className="h-[120px] w-full" />
                    </div>
                  </div>
                ) : (
                  <div
                    className={
                      effectiveShouldShowGenerateOverlay
                        ? "blur-sm pointer-events-none"
                        : ""
                    }
                  >
                    <StrategyContent
                      selectedCluster={selectedCluster}
                      currentBrand={currentBrand}
                      snapshot={strategySnapshot}
                      planTasks={
                        isSelectedClusterFullVisibilityBlocked ? [] : planTasks
                      }
                      isPlanLoading={isPlanLoading}
                      isFullVisibilityCluster={
                        isSelectedClusterFullVisibilityBlocked
                      }
                      isPdfExporting={isPdfExporting}
                      onTasksSectionSeen={handleTasksSectionSeen}
                    />
                  </div>
                )}
                {effectiveShouldShowGenerateOverlay ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/55 backdrop-blur-[2px]">
                    <div className="flex w-[calc(100%-24px)] max-w-[460px] flex-col items-center gap-3 rounded-md bg-white px-4 py-5 text-center sm:px-6 sm:py-7">
                      <div className="p-2 rounded-lg bg-[#FFEEE5]">
                        <Compass className="text-[#FF5E00]" />
                      </div>
                      <div className="text-[#0A0A0A] text-2xl leading-8 font-semibold sm:text-[36px] sm:leading-[42px]">
                        <LocalizedText id="legacy.your.ai.visibility.strategy.1hz77nl" />
                      </div>
                      <div className="text-sm text-[#737373]">
                        <LocalizedText id="legacy.a.step.by.step.plan.to.help.your.1k8hkhx" />
                      </div>
                      <span
                        className={
                          canClickGenerate
                            ? "relative inline-flex max-w-full shrink-0"
                            : "inline-flex max-w-full shrink-0"
                        }
                      >
                        {canClickGenerate ? (
                          <svg
                            className="pointer-events-none absolute -inset-1 h-[calc(100%+8px)] w-[calc(100%+8px)]"
                            viewBox="0 0 100 40"
                            preserveAspectRatio="none"
                          >
                            <rect
                              x="2"
                              y="2"
                              width="96"
                              height="36"
                              rx="6"
                              fill="none"
                              stroke="#FF5E00"
                              strokeWidth="2"
                              strokeDasharray="20 234"
                              strokeLinecap="round"
                              style={{ filter: "blur(1.5px)" }}
                              className="border-loader"
                            />
                            <rect
                              x="2"
                              y="2"
                              width="96"
                              height="36"
                              rx="6"
                              fill="none"
                              stroke="#FF5E00"
                              strokeWidth="2"
                              strokeDasharray="20 234"
                              strokeLinecap="round"
                              className="border-loader"
                            />
                          </svg>
                        ) : null}
                        <Button
                          className={`h-10 px-4 ${
                            canClickGenerate
                              ? "cursor-pointer bg-[#0A0A0A] text-[#FAFAFA] hover:bg-[#171717]"
                              : "cursor-not-allowed bg-[#E5E5E5] text-[#A3A3A3] hover:bg-[#E5E5E5]"
                          } ${canClickGenerate ? "relative z-10" : ""}`}
                          onClick={handleRegenerate}
                          disabled={!canClickGenerate}
                        >
                          <LocalizedText id="legacy.generate.strategy.vjq1xx" />
                        </Button>
                      </span>
                      {!canClickGenerate && regenerateDisabledReason ? (
                        <div className="max-w-[320px] text-xs text-[#737373]">
                          {regenerateDisabledReason}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : clustersFetching ? (
            <div className="w-full h-full flex justify-center items-center">
              <div className="w-full">{defaultPageLoader}</div>
            </div>
          ) : (
            <div className="relative overflow-hidden h-full">
              <img src={blurIcon} className="w-full h-full" />
              <div className="absolute w-full flex flex-col text-center items-center gap-3 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="p-2 rounded-lg bg-[#FFEEE5]">
                  <Compass className="text-[#FF5E00]" />
                </div>
                <div className="text-[#0A0A0A] text-xl sm:text-2xl font-semibold">
                  <LocalizedText id="legacy.your.ai.visibility.strategy.1hz77nl" />
                </div>
                <div className="text-sm text-[#737373]">
                  <LocalizedText id="legacy.a.step.by.step.plan.to.help.your.1k8hkhx" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={isBoostLimitModalOpen}
        onOpenChange={setIsBoostLimitModalOpen}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[425px] gap-4 border-[#E5E5E5] p-4 sm:max-w-[425px] sm:p-4"
        >
          <DialogHeader className="items-center gap-2 text-center sm:text-center">
            <TriangleAlert size={28} className="text-[#FF5E00]" />
            <DialogTitle className="text-xl leading-7 font-semibold">
              <LocalizedText id="legacy.boost.limit.reached.6n7wi1" />
            </DialogTitle>
            <DialogDescription className="mx-auto max-w-[375px] text-center leading-6">
              <span className="block">
                <LocalizedText id="legacy.your.trial.includes.boost.actions.for.1.cluster.10li0v" />
              </span>
              <span className="block">
                <LocalizedText id="legacy.upgrade.to.boost.more.clusters.and.unlock.full.6gk8jp" />
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-hidden rounded-lg border border-[#E5E5E5]">
            <div className="divide-y divide-[#E5E5E5] px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <ChartNoAxesColumnIncreasing size={16} />
                  <LocalizedText id="legacy.boost.unlimited.clusters.1ayeniu" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <ScanEye size={16} />
                  <LocalizedText id="legacy.full.visibility.analytics.19jp1i" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <Zap size={16} />
                  <LocalizedText id="legacy.higher.usage.limits.1tpejxb" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
            </div>
            <div className="border-t border-[#E5E5E5] px-4 py-1 text-center text-xs text-[#737373]">
              <LocalizedText id="legacy.and.more.features.h73j0y" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              className="cursor-pointer bg-[#FF5E00] text-white hover:bg-[#ff8138]"
              onClick={() => {
                const brandSearch = resolvedBrandId
                  ? `?brandId=${resolvedBrandId}`
                  : "";
                capturePosthogEvent("upgrade_clicked", {
                  modal: "trial_boost_limit",
                  brand_id: resolvedBrandId,
                  org_id: resolvedOrgId,
                });
                setIsBoostLimitModalOpen(false);
                navigate(`/dashboard/plans-pricing${brandSearch}`);
              }}
            >
              <LocalizedText id="legacy.see.plans.and.upgrade.11ooxj4" />
            </Button>
            <Button
              variant="outline"
              className="w-fit cursor-pointer self-center"
              onClick={() => setIsBoostLimitModalOpen(false)}
            >
              <LocalizedText id="legacy.not.now.1f5o0g6" />
            </Button>
          </div>

          <div className="text-center text-xs text-[#737373]">
            <LocalizedText id="legacy.need.help.or.a.custom.plan.16lhm8t" />{" "}
            <button
              className="text-[#0A0A0A] underline underline-offset-2"
              onClick={() => {
                setIsBoostLimitModalOpen(false);
                navigate("/dashboard/support");
              }}
            >
              <LocalizedText id="legacy.contact.us.14c3h4z" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
